<template>
  <div class="container">
    <div class="modal-order-1">
      <div class="row justify-center md:justify-start items-center md:items-start">
        <div class="md:col-6">
          <p v-html="getParams.product.description" />
        </div>
        <div class="col-12 md:col-6 mb-3 md:mb-0">
          <div class="product-hero__image mb-0 w-100 h-auto">
            <div
              class="product-hero__image__in modal-hero-img embed-responsive"
              :class="{ 'no-padding': !getParams.product.images[0]?.has_padding }"
            >
              <RemoteResponsiveImage
                v-if="getParams.product.images.length > 0"
                :product-image-data="getParams.product.images[0]?.data"
                :img-class="`embed-responsive-item ${
                  getParams.product.images[0]?.has_padding ? 'image-contain' : 'image-cover'
                }`"
                :alt="getParams.product.name"
              />
            </div>
          </div>
        </div>
        <div class="col-12">
          <h3>{{ t('composition') }}:</h3>
          <p v-html="getParams.product.composition" />
        </div>
      </div>
    </div>
    <div class="row justify-center">
      <div
        v-if="getParams.product.showNutrition"
        class="col-12"
      >
        <h2 class="mb-4 md:mb-5 lg:mb-6 text-primary text-center">
          {{ t('nutritional_information') }}
        </h2>
        <table class="table-base table-striped border-bottom">
          <tbody>
            <tr>
              <td>{{ t('energy_value') }}</td>
              <td class="font-bold text-nowrap">
                {{ getParams.product.nutrition.energy }} kJ /
                {{ (getParams.product.nutrition.energy / 4.186).toFixed(2) }} kcal
              </td>
            </tr>
            <tr>
              <td>{{ t('fats') }}</td>
              <td class="font-bold text-nowrap">
                {{ getParams.product.nutrition.fats }} g
              </td>
            </tr>
            <tr>
              <td>{{ t('of_which_saturated_fatty_acids') }}</td>
              <td class="font-bold text-nowrap">
                {{ getParams.product.nutrition.saturatedFats }} g
              </td>
            </tr>
            <tr>
              <td>{{ t('carbohydrates') }}</td>
              <td class="font-bold text-nowrap">
                {{ getParams.product.nutrition.carbohydrates }} g
              </td>
            </tr>
            <tr>
              <td>{{ t('of_which_sugars') }}</td>
              <td class="font-bold text-nowrap">
                {{ getParams.product.nutrition.sugars }} g
              </td>
            </tr>
            <tr>
              <td>{{ t('fiber') }}</td>
              <td class="font-bold text-nowrap">
                {{ getParams.product.nutrition.fibre }} g
              </td>
            </tr>
            <tr>
              <td>{{ t('proteins') }}</td>
              <td class="font-bold text-nowrap">
                {{ getParams.product.nutrition.proteins }} g
              </td>
            </tr>
            <tr>
              <td>{{ t('salt') }}</td>
              <td class="font-bold text-nowrap">
                {{ getParams.product.nutrition.salt }} g
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="text-center">
        <router-link
          class="btn btn-success  mt-2 py-3 px-8 xl:px-9 rounded-pill font-medium"
          :to="{ name: 'productPath', params: { permalink: getParams.product.permalink } }"
        >
          {{ t('read_more') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import { useModalStore } from '@/stores/ModalStore'

const { t } = useI18n()

const modalStore = useModalStore()
const { getParams } = storeToRefs(modalStore)
</script>

<i18n>
  {
    "cs": {
    "carbohydrates": "Sacharidy",
    "composition": "Složení",
    "energy_value": "Energetická hodnota",
    "fats": "Tuky",
    "fiber": "Vláknina",
    "nutritional_information": "Výživové údaje na 100 g",
    "of_which_saturated_fatty_acids": "z toho nasycené mastné kyseliny",
    "of_which_sugars": "z toho cukry",
    "proteins": "Bílkoviny",
    "read_more": "číst více",
    "salt": "Sůl"
},
    "sk": {
    "carbohydrates": "Sacharidy",
    "composition": "Zloženie",
    "energy_value": "Energia",
    "fats": "Tuky",
    "fiber": "Vláknina",
    "nutritional_information": "Výživové údaje na 100 g",
    "of_which_saturated_fatty_acids": "z toho nasýtené mastné kyseliny",
    "of_which_sugars": "z toho cukry",
    "proteins": "Bielkoviny",
    "read_more": "čítať ďalej",
    "salt": "Soľ"
},
    "pl": {
    "carbohydrates": "Węglowodany",
    "composition": "Skład",
    "energy_value": "Wartość energetyczna",
    "fats": "Tłuszcz",
    "fiber": "Błonnik",
    "nutritional_information": "Wartości odżywcze w 100 g",
    "of_which_saturated_fatty_acids": "w tym kwasy tłuszczowe nasycone",
    "of_which_sugars": "w tym cukry",
    "proteins": "Białko",
    "read_more": "zobacz więcej",
    "salt": "Sól"
},
    "hu": {
    "carbohydrates": "Szénhidrátok",
    "composition": "Összetevők",
    "energy_value": "Energiatartalom",
    "fats": "Zsírok",
    "fiber": "Rostok",
    "nutritional_information": "Tápértékadatok 100 g",
    "of_which_saturated_fatty_acids": "ebből telített zsírsavak",
    "of_which_sugars": "ebből cukror",
    "proteins": "Fehérjék",
    "read_more": "többet mutat",
    "salt": "Só"
},
    "de": {
    "carbohydrates": "Kohlenhydrate",
    "composition": "Zutaten",
    "energy_value": "Energiewert",
    "fats": "Fette",
    "fiber": "Ballaststoffe",
    "nutritional_information": "Nährwertangaben pro 100 g",
    "of_which_saturated_fatty_acids": "davon gesättigte Fettsäuren",
    "of_which_sugars": "davon Zucker",
    "proteins": "Eiweiß",
    "read_more": "Mehr lesen",
    "salt": "Salz"
},
    "at": {
    "carbohydrates": "Kohlenhydrate",
    "composition": "Zutaten",
    "energy_value": "Energiewert",
    "fats": "Fette",
    "fiber": "Ballaststoffe",
    "nutritional_information": "Nährwertangaben pro 100 g",
    "of_which_saturated_fatty_acids": "davon gesättigte Fettsäuren",
    "of_which_sugars": "davon Zucker",
    "proteins": "Eiweiß",
    "read_more": "Mehr lesen",
    "salt": "Salz"
},
    "ro": {
    "carbohydrates": "Carbohidrați",
    "composition": "Ingrediente",
    "energy_value": "Valoare energetică",
    "fats": "Grăsimi",
    "fiber": "Fibre",
    "nutritional_information": "Informații nutriționale 100 g",
    "of_which_saturated_fatty_acids": "din care acizi grași saturați",
    "of_which_sugars": "din care zaharuri",
    "proteins": "Proteine",
    "read_more": "mai mult",
    "salt": "Sare"
},
    "fr": {
    "carbohydrates": "Glucides",
    "composition": "Composition",
    "energy_value": "Valeur énergétique",
    "fats": "Graisses",
    "fiber": "Fibres alimentaires",
    "nutritional_information": "Valeurs nutritionnelles pour 100 g",
    "of_which_saturated_fatty_acids": "dont acides gras saturés",
    "of_which_sugars": "dont sucres",
    "proteins": "Protéines",
    "read_more": "en savoir plus",
    "salt": "Sel"
}
  }
</i18n>
